import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  photoUrl?: string;
  role: 'agent' | 'team_leader' | 'admin';
  hierarchy: { upline?: string; level: number };
  mfaEnabled?: boolean;
  passkeyRegistered?: boolean;
  biometricRegistered?: boolean;
  designation?: string;
  designationAbbr?: string;
  commissionLevel?: string;
  tier?: 'starter' | 'producer' | 'leader' | 'executive';
  avatarSeed?: number;
}

export type AuthStep = 'idle' | 'credentials' | 'mfa' | 'authenticated' | 'locked' | 'session-expired';

interface AuthContextState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authStep: AuthStep;
  sessionExpired: boolean;
  showTimeoutWarning: boolean;
  timeoutCountdown: number;
  login: (email: string, password: string) => Promise<{ requiresMfa: boolean }>;
  verifyMfa: (code: string) => Promise<boolean>;
  loginWithPasskey: () => Promise<boolean>;
  loginWithBiometric: () => Promise<boolean>;
  logout: () => void;
  reLogin: (password: string) => Promise<boolean>;
  extendSession: () => void;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextState | undefined>(undefined);

// Session config (prototype values — shorter for demo)
const SESSION_TIMEOUT_MS = 15 * 60 * 1000;      // 15 min idle timeout
const WARNING_BEFORE_MS = 2 * 60 * 1000;         // warn 2 min before
const SESSION_CHECK_INTERVAL = 1000;              // check every second

interface AuthProviderProps {
  children: ReactNode;
  initialUser?: User | null;
}

export function AuthProvider({ children, initialUser = null }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(initialUser);
  const [isLoading, setIsLoading] = useState(false);
  const [authStep, setAuthStep] = useState<AuthStep>(initialUser ? 'authenticated' : 'idle');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [timeoutCountdown, setTimeoutCountdown] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Track user activity
  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (showTimeoutWarning) {
      setShowTimeoutWarning(false);
    }
  }, [showTimeoutWarning]);

  // Session timeout checker
  useEffect(() => {
    if (authStep !== 'authenticated') {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    timerRef.current = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;
      const remaining = SESSION_TIMEOUT_MS - idle;

      if (remaining <= 0) {
        // Session expired
        setShowTimeoutWarning(false);
        setSessionExpired(true);
        setAuthStep('session-expired');
        if (timerRef.current) clearInterval(timerRef.current);
      } else if (remaining <= WARNING_BEFORE_MS) {
        setShowTimeoutWarning(true);
        setTimeoutCountdown(Math.ceil(remaining / 1000));
      } else {
        setShowTimeoutWarning(false);
      }
    }, SESSION_CHECK_INTERVAL);

    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [authStep]);

  // Listen for user activity events
  useEffect(() => {
    if (authStep !== 'authenticated') return;
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetActivity));
    return () => events.forEach(e => window.removeEventListener(e, resetActivity));
  }, [authStep, resetActivity]);

  // Dev tool event listener (prototype only)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.action === 'show-timeout-warning') {
        setShowTimeoutWarning(true);
        setTimeoutCountdown(120);
      } else if (detail?.action === 'force-session-expired') {
        setShowTimeoutWarning(false);
        setSessionExpired(true);
        setAuthStep('session-expired');
      } else if (detail?.action === 'login-as-persona' && detail.persona) {
        const p = detail.persona;
        setUser({
          id: p.id,
          firstName: p.firstName,
          lastName: p.lastName,
          email: p.email,
          role: p.role,
          hierarchy: { level: 3 },
          mfaEnabled: true,
          passkeyRegistered: true,
          biometricRegistered: true,
          designation: p.designation,
          designationAbbr: p.designationAbbr,
          commissionLevel: p.commissionLevel,
          tier: p.tier,
          avatarSeed: p.avatarSeed,
        });
        setAuthStep('authenticated');
        setSessionExpired(false);
        setShowTimeoutWarning(false);
        lastActivityRef.current = Date.now();
      }
    };
    window.addEventListener('auth-dev-tool', handler);
    return () => window.removeEventListener('auth-dev-tool', handler);
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<{ requiresMfa: boolean }> => {
    setIsLoading(true);
    try {
      await new Promise(r => setTimeout(r, 800));
      // Simulate: any non-empty credentials work
      if (!email || !password) throw new Error('Invalid credentials');

      const mockUser: User = {
        id: '1',
        firstName: 'Sarah',
        lastName: 'Chen',
        email,
        phone: '(555) 123-4567',
        role: 'agent',
        hierarchy: { level: 3 },
        mfaEnabled: true,
        passkeyRegistered: true,
        biometricRegistered: true,
      };
      setUser(mockUser);

      if (mockUser.mfaEnabled) {
        setAuthStep('mfa');
        return { requiresMfa: true };
      }

      setAuthStep('authenticated');
      lastActivityRef.current = Date.now();
      setSessionExpired(false);
      return { requiresMfa: false };
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const verifyMfa = useCallback(async (code: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      await new Promise(r => setTimeout(r, 600));
      // Prototype: accept any 6-digit code
      if (code.length === 6 && /^\d+$/.test(code)) {
        setAuthStep('authenticated');
        lastActivityRef.current = Date.now();
        setSessionExpired(false);
        return true;
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loginWithPasskey = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    try {
      await new Promise(r => setTimeout(r, 1200));
      // Simulate WebAuthn passkey flow
      const mockUser: User = {
        id: '1', firstName: 'Sarah', lastName: 'Chen',
        email: 'sarah.chen@wfg.com', role: 'agent',
        hierarchy: { level: 3 }, mfaEnabled: true,
        passkeyRegistered: true, biometricRegistered: true,
      };
      setUser(mockUser);
      setAuthStep('authenticated');
      lastActivityRef.current = Date.now();
      setSessionExpired(false);
      return true;
    } catch {
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loginWithBiometric = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    try {
      await new Promise(r => setTimeout(r, 1000));
      // Simulate biometric (Face ID / fingerprint) flow
      const mockUser: User = {
        id: '1', firstName: 'Sarah', lastName: 'Chen',
        email: 'sarah.chen@wfg.com', role: 'agent',
        hierarchy: { level: 3 }, mfaEnabled: true,
        passkeyRegistered: true, biometricRegistered: true,
      };
      setUser(mockUser);
      setAuthStep('authenticated');
      lastActivityRef.current = Date.now();
      setSessionExpired(false);
      return true;
    } catch {
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setAuthStep('idle');
    setSessionExpired(false);
    setShowTimeoutWarning(false);
  }, []);

  const reLogin = useCallback(async (password: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      await new Promise(r => setTimeout(r, 600));
      if (password.length > 0) {
        setAuthStep('authenticated');
        setSessionExpired(false);
        lastActivityRef.current = Date.now();
        return true;
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const extendSession = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowTimeoutWarning(false);
  }, []);

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser(prev => prev ? { ...prev, ...updates } : null);
  }, []);

  const value: AuthContextState = {
    user, isAuthenticated: authStep === 'authenticated', isLoading, authStep,
    sessionExpired, showTimeoutWarning, timeoutCountdown,
    login, verifyMfa, loginWithPasskey, loginWithBiometric,
    logout, reLogin, extendSession, updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
